import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ProjectApi from "Api/ProjectApi/ProjectApi";
import {
  errorNotification,
  loadingNotification,
  successNotification,
  updateToError,
  updateToSuccessNotification,
} from "Components/MyNotification";
import { v4 as uuidv4 } from "uuid";
import { setLoading } from "../MainSlice/MainSlice";

export const createProjectThunk = createAsyncThunk(
  "project/createNew",
  async (_, thunkAPI) => {
    const toastId = loadingNotification("Створення проекту...");

    try {
      const { projectGroups, title, users, workTypes, accountNumber } =
        thunkAPI.getState().project;
      if (!title) {
        updateToError(toastId, "Має бути заповнена хоча б назва проекту");
        return thunkAPI.rejectWithValue("Не передані всі поля");
      }

      const result = await ProjectApi.createNewProject({
        projectGroups,
        title,
        users,
        workTypes,
        accountNumber,
      });

      if (result.status === "fail") {
        updateToError(toastId, result.message);
        return thunkAPI.rejectWithValue(result.message);
      }

      updateToSuccessNotification(toastId, "Створено новий проект");
      return result.dto;
    } catch (err) {
      updateToError(toastId, err.message || "Помилка створення");
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const updateProjectThunk = createAsyncThunk(
  "project/updateProject",
  async (_, thunkAPI) => {
    const toastId = loadingNotification("Оновлення проекту...");

    try {
      const {
        projectGroups,
        title,
        users,
        workTypes,
        currentProjectId,
        accountNumber,
      } = thunkAPI.getState().project;
      if (!title) {
        updateToError(toastId, "Має бути заповнена хоча б назва проекту");
        return thunkAPI.rejectWithValue("Не передані всі поля");
      }

      const result = await ProjectApi.updateProject({
        projectGroups,
        title,
        users,
        workTypes,
        projectId: currentProjectId,
        accountNumber,
      });

      if (result.status === "fail") {
        updateToError(toastId, result.message);
        return thunkAPI.rejectWithValue(result.message);
      }

      updateToSuccessNotification(toastId, "Дані проекту оновлено");
      return result.dto;
    } catch (err) {
      updateToError(toastId, err.message || "Помилка оновлення");
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const updateProjectMaterialsThunk = createAsyncThunk(
  "project/updateProjectMaterials",
  async (_, thunkAPI) => {
    const toastId = loadingNotification("Оновлення матеріалів проекту...");

    try {
      const { projectMaterialsForTable, currentProjectId } =
        thunkAPI.getState().project;

      const result = await ProjectApi.updateProjectMaterials({
        projectMaterialsForTable,
        projectId: currentProjectId,
      });

      if (result.status === "fail") {
        updateToError(toastId, result.message);
        return thunkAPI.rejectWithValue(result.message);
      }

      updateToSuccessNotification(toastId, "Матеріали проекту оновлено");
      return result.dto;
    } catch (err) {
      updateToError(toastId, err.message || "Помилка оновлення");
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const getAllStatusesThunk = createAsyncThunk(
  "projects/getAllStatuses",
  async (_, thunkAPI) => {
    try {
      const result = await ProjectApi.getAllStatuses();

      if (result.status === "fail") {
        errorNotification(result.message);
        return thunkAPI.rejectWithValue(result.message);
      }
      if (result.status === "success") {
        return result.dto;
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const getAllProjectsThunk = createAsyncThunk(
  "projects/getAllProjects",
  async (data, thunkAPI) => {
    try {
      const page = data?.page || 1;
      const searchValue = data?.searchValue || "";
      const ingeneerIdArray = data?.usersFilter || [];
      const result = await ProjectApi.getAllProjects(
        page,
        searchValue,
        ingeneerIdArray,
      );
      if (result.status === "fail") {
        errorNotification(result.message);
        return thunkAPI.rejectWithValue(result.message);
      }
      if (result.status === "success") {
        return result.dto;
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const getProjectInfoThunk = createAsyncThunk(
  "projects/getProjectInfo",
  async (projectId, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoading(true));
      const result = await ProjectApi.getProject(projectId);
      if (result.status === "fail") {
        errorNotification(result.message);
        thunkAPI.dispatch(setLoading(false));
        return thunkAPI.rejectWithValue(result.message);
      }
      if (result.status === "success") {
        thunkAPI.dispatch(setLoading(false));
        return result.dto;
      }
    } catch (err) {
      thunkAPI.dispatch(setLoading(false));
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const setFinishProjectThunk = createAsyncThunk(
  "projects/setFinish",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoading(true));
      const { currentProjectId } = thunkAPI.getState().project;
      const result = await ProjectApi.setFinish(currentProjectId);

      if (result.status === "fail") {
        errorNotification(result.message);
        thunkAPI.dispatch(setLoading(false));
        return thunkAPI.rejectWithValue(result.message);
      }
      if (result.status === "success") {
        successNotification("Проект завершено");
        thunkAPI.dispatch(setLoading(false));
        return result.dto;
      }
    } catch (err) {
      thunkAPI.dispatch(setLoading(false));
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const getMyProjectsThunk = createAsyncThunk(
  "projects/getMyProjects",
  async (data, thunkAPI) => {
    try {
      const page = data?.page || 1;
      const searchValue = data?.searchValue || "";
      const result = await ProjectApi.getMyProjects(page, searchValue);
      if (result.status === "fail") {
        errorNotification(result.message);
        return thunkAPI.rejectWithValue(result.message);
      }
      if (result.status === "success") {
        return result.dto;
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const getWorksTableProjectThunk = createAsyncThunk(
  "works/getProjectWorksTable",
  async (_, thunkAPI) => {
    try {
      const { currentProjectId } = thunkAPI.getState().project;
      const result = await ProjectApi.getWorksForProject(currentProjectId);
      if (result.status === "fail") {
        errorNotification(result.message);
        return thunkAPI.rejectWithValue(result.message);
      }
      if (result.status === "success") {
        return result.dto;
      }
    } catch (err) {
      errorNotification(err.message);
      return thunkAPI.rejectWithValue(err);
    }
  },
);
export const getTableMaterialsForProjectThunk = createAsyncThunk(
  "works/getTableMaterialsForProject",
  async (_, thunkAPI) => {
    try {
      const { currentProjectId } = thunkAPI.getState().project;
      const result = await ProjectApi.getTableMaterialsForProject({
        projectId: currentProjectId,
      });
      if (result.status === "fail") {
        errorNotification(result.message);
        return thunkAPI.rejectWithValue(result.message);
      }
      if (result.status === "success") {
        return result.dto;
      }
    } catch (err) {
      errorNotification(err.message);
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const postEstimateFileProjectWorksThunk = createAsyncThunk(
  "works/postFileWorksEstimate",
  async (file, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const result = await ProjectApi.postEstimateFileWorks(formData);

      if (result.status === "fail") {
        errorNotification(result.message);
        return thunkAPI.rejectWithValue(result.message);
      }
      if (result.status === "success") {
        if (result.dto.length === 0) {
          errorNotification(
            "Не розпізнано роботи, або дані роботи не завантажені в базу",
          );
        } else {
          successNotification("Отримано роботи з кошторису");
        }
        return result.dto;
      }
    } catch (err) {
      errorNotification(err.message);
      return thunkAPI.rejectWithValue(err);
    }
  },
);

const initialState = {
  title: "",
  accountNumber: "",
  projectGroups: [],
  //   [
  //   {
  //     "id": "group-uuid-1",
  //     "title": "Group 1",
  //   },
  //   {
  //     "id": "group-uuid-2",
  //     "title": "Group 2",
  //   }
  // ]
  workTypes: [],
  //   [
  //     {
  //       "id": "wt-1",
  //       "code": "EARTH_EXC",
  //       "title": "Земляні роботи",
  //       "unit": "м3",

  //       "allowedWorkType": {
  //         "id": "awt-1",
  //         "plannedValue": 500,
  //         "groupId": "group-1"
  //       }
  //     },
  //     {
  //       "id": "wt-2",
  //       "code": "FOUND_CONC",
  //       "title": "Бетонування фундаменту",
  //       "unit": "м3",

  //       "allowedWorkType": {
  //          "id": "awt-2",
  //          "plannedValue": 120,
  //           "groupId": null
  //       }
  //     }
  //   ]
  users: {},
  //  {
  //   "some roleId": [
  //     { "id": "user-1", "name": "Іван", "email":"hello@gmail.com"},
  //     { "id": "user-2", "name": "Олег", "email":"hello2@gmail.com"}
  //   ]
  //  }
  //  {
  //   "some roleId": [
  //     { "id": "user-1", "name": "Іван", "email":"hello@gmail.com"},
  //   ]
  //  }
  //
  //
  //
  projectStatus: [],
  createdAt: "",
  updatedAt: "",
  createdBy: "",
  allStatuses: [],
  projectsList: [],
  isLoading: false,
  currentPage: 1,
  total: 1,
  totalPages: 1,
  isSomeProjectValuesChanged: false,
  currentProjectId: null,
  projectWorksForTable: [],
  isUserCreatorOfThisProject: false,
  userRolesForThisProject: [],
  projectMaterialsForTable: [],
  isMaterialsChanged: false,
};

const ProjectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    setTitle: (state, action) => {
      state.title = action.payload;
    },
    setCreateGroup: (state, action) => {
      state.projectGroups.push(action.payload);
    },
    setDeleteGroup: (state, action) => {
      state.projectGroups = [...state.projectGroups].filter(
        (el) => el.id !== action.payload,
      );
      state.workTypes = [...state.workTypes].map((el) => {
        if (el.allowedWorkType.groupId === action.payload) {
          el.allowedWorkType.groupId = null;
        }
        return el;
      });
    },
    setDeleteAllowedWork: (state, action) => {
      const { workId, allowedWorkId } = action.payload;
      state.workTypes = [...state.workTypes].filter((el) => {
        if (el.id !== workId && el.allowedWorkType.id !== allowedWorkId) {
          return el;
        }
      });
    },
    setChangePlannedValue: (state, action) => {
      const { workId, value, allowedWorkId } = action.payload;

      state.workTypes = [...state.workTypes].map((el) => {
        if (el.id === workId && el.allowedWorkType.id === allowedWorkId) {
          el.allowedWorkType.plannedValue = value;
        }
        return el;
      });
    },
    setAllowedWorkType: (state, action) => {
      const { id, code, unit, title, plannedValue = 0 } = action.payload;
      const isInState = state.workTypes.some((el) => el.id === id);
      if (!isInState) {
        state.workTypes.push({
          id,
          code,
          unit,
          title,
          allowedWorkType: {
            id: uuidv4(),
            plannedValue: plannedValue,
            groupId: null,
          },
        });
      }
    },
    setChangeGroup: (state, action) => {
      const { groupId, itemId, allowedWorkId } = action.payload;
      state.workTypes = [...state.workTypes].map((el) => {
        if (el.id === itemId && el.allowedWorkType.id === allowedWorkId) {
          el.allowedWorkType.groupId = groupId;
        }
        return el;
      });
    },
    setChangeUsers: (state, action) => {
      const { role, users } = action.payload;
      state.users[role] = users;
    },
    setChangeAccountNumber: (state, action) => {
      state.accountNumber = action.payload;
    },
    setIsParamsChanged: (state, action) => {
      state.isSomeProjectValuesChanged = true;
    },
    setResetProjectData: (state, action) => {
      state.currentProjectId = null;
      state.title = "";
      state.accountNumber = "";
      state.users = {};
      state.workTypes = [];
      state.projectGroups = [];
      state.projectStatus = [];
      state.isSomeProjectValuesChanged = false;
      state.projectWorksForTable = [];
      state.isUserCreatorOfThisProject = false;
      state.userRolesForThisProject = [];
      state.projectMaterialsForTable = [];
      state.isMaterialsChanged = false;
    },
    setMaterialsForProject: (state, action) => {
      const result = action.payload;
      const prev = [...state.projectMaterialsForTable];
      const data = result.filter((elem) => {
        const isAvailable = [...prev].some(
          (el) =>
            el.dateArrived === elem.dateArrived &&
            el.nomenclature === elem.nomenclature,
        );
        return !isAvailable;
      });
      state.projectMaterialsForTable = [...prev, ...data];
      state.isMaterialsChanged = true;
    },
    changeWorkTypeTiedForMaterials: (state, action) => {
      const { materialId, workTypeTied, materialNomenclature } = action.payload;
      state.projectMaterialsForTable = [...state.projectMaterialsForTable].map(
        (elem) => {
          if (
            elem.id === materialId
            // ||
            // elem.nomenclature === materialNomenclature
          ) {
            elem.workTypeTied = workTypeTied;
          }
          return elem;
        },
      );
      state.isMaterialsChanged = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllStatusesThunk.fulfilled, (state, action) => {
      state.allStatuses = action.payload;
    });
    builder.addCase(getMyProjectsThunk.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(getMyProjectsThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      const { total, data, currentPage, totalPages } = action.payload;

      state.projectsList = data;
      state.currentPage = currentPage;
      state.total = total;
      state.totalPages = totalPages;
    });
    builder.addCase(getMyProjectsThunk.rejected, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(getAllProjectsThunk.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(getAllProjectsThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      const { total, data, currentPage, totalPages } = action.payload;

      state.projectsList = data;
      state.currentPage = currentPage;
      state.total = total;
      state.totalPages = totalPages;
    });
    builder.addCase(getAllProjectsThunk.rejected, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(createProjectThunk.fulfilled, (state, action) => {
      state.title = "";
      state.accountNumber = "";
      state.users = {};
      state.workTypes = [];
      state.projectGroups = [];
    });
    builder.addCase(getProjectInfoThunk.fulfilled, (state, action) => {
      const {
        title,
        users,
        workTypes,
        projectGroups,
        id,
        projectStatus,
        isUserCreatorOfThisProject,
        userRolesForThisProject,
        materials = [],
        accountNumber,
      } = action.payload;
      state.title = title;
      state.accountNumber = accountNumber || "";
      state.users = users;
      state.workTypes = workTypes;
      state.projectGroups = projectGroups;
      state.currentProjectId = id;
      state.projectStatus = projectStatus;
      state.isSomeProjectValuesChanged = false;
      state.projectMaterialsForTable = materials;
      state.isUserCreatorOfThisProject = isUserCreatorOfThisProject;
      state.userRolesForThisProject = userRolesForThisProject;
      state.isMaterialsChanged = false;
    });
    builder.addCase(getProjectInfoThunk.rejected, (state, action) => {
      state.title = "";
      state.accountNumber = "";
      state.users = {};
      state.workTypes = [];
      state.projectGroups = [];
      state.currentProjectId = null;
      state.projectStatus = [];
      state.isSomeProjectValuesChanged = false;
      state.isUserCreatorOfThisProject = false;
      state.userRolesForThisProject = [];
      state.projectMaterialsForTable = [];
      state.isMaterialsChanged = false;
    });
    builder.addCase(updateProjectThunk.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(updateProjectThunk.fulfilled, (state, action) => {
      state.isSomeProjectValuesChanged = false;
      state.isMaterialsChanged = false;
      state.isLoading = false;
    });
    builder.addCase(updateProjectThunk.rejected, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(updateProjectMaterialsThunk.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(updateProjectMaterialsThunk.fulfilled, (state, action) => {
      state.isSomeProjectValuesChanged = false;
      state.isMaterialsChanged = false;
      state.isLoading = false;
    });
    builder.addCase(updateProjectMaterialsThunk.rejected, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(setFinishProjectThunk.fulfilled, (state, action) => {
      state.currentProjectId = null;
      state.title = "";
      state.accountNumber = "";
      state.users = {};
      state.workTypes = [];
      state.projectGroups = [];
      state.projectStatus = [];
      state.isSomeProjectValuesChanged = false;
      state.isUserCreatorOfThisProject = false;
      state.userRolesForThisProject = [];
      state.projectMaterialsForTable = [];
      state.isMaterialsChanged = false;
    });
    builder.addCase(getWorksTableProjectThunk.pending, (state, action) => {
      state.isLoading = true;
    });
    builder.addCase(getWorksTableProjectThunk.fulfilled, (state, action) => {
      state.projectWorksForTable = action.payload;
      state.isLoading = false;
    });
    builder.addCase(getWorksTableProjectThunk.rejected, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(
      getTableMaterialsForProjectThunk.pending,
      (state, action) => {
        state.isLoading = true;
      },
    );
    builder.addCase(
      getTableMaterialsForProjectThunk.fulfilled,
      (state, action) => {
        state.projectMaterialsForTable = action.payload;
        state.isMaterialsChanged = false;
        state.isLoading = false;
      },
    );
    builder.addCase(
      getTableMaterialsForProjectThunk.rejected,
      (state, action) => {
        state.isLoading = false;
      },
    );
    builder.addCase(
      postEstimateFileProjectWorksThunk.pending,
      (state, action) => {
        state.isLoading = true;
      },
    );
    builder.addCase(
      postEstimateFileProjectWorksThunk.fulfilled,
      (state, action) => {
        state.isLoading = false;

        action.payload.forEach((elem) => {
          const { id, code, unit, title, plannedValue = 0 } = elem;
          const isInState = state.workTypes.some((el) => el.id === id);
          if (!isInState) {
            state.workTypes.push({
              id,
              code,
              unit,
              title,
              allowedWorkType: {
                id: uuidv4(),
                plannedValue: plannedValue,
                groupId: null,
              },
            });
          }
          state.isSomeProjectValuesChanged = true;
        });
      },
    );
    builder.addCase(
      postEstimateFileProjectWorksThunk.rejected,
      (state, action) => {
        state.isLoading = false;
      },
    );
  },
});

export const {
  setTitle,
  setCreateGroup,
  setDeleteGroup,
  setDeleteAllowedWork,
  setChangePlannedValue,
  setAllowedWorkType,
  setChangeGroup,
  setChangeUsers,
  setIsParamsChanged,
  setResetProjectData,
  setMaterialsForProject,
  changeWorkTypeTiedForMaterials,
  setChangeAccountNumber,
} = ProjectSlice.actions;
export const selectProject = (state) => state.project;
export default ProjectSlice.reducer;
